import { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react'
import type { Song } from '../../lib/api'
import { authService } from '../../lib/authService'

// API Base URL - production uses relative URLs (nginx proxies to backend)
// Test environment uses localhost for direct backend access
const API_BASE_URL = typeof process !== 'undefined' && process.env.NODE_ENV === 'test'
  ? 'http://localhost:8001'
  : ''  // Empty string = relative URLs, proxied by nginx in production
// SectionNavigation and SectionSidebar removed for cleaner interface
import RichTextLyricsEditor, { type RichTextLyricsEditorRef } from '../RichTextLyricsEditor'
import WordCollectionSidebar from '../wordCollection/WordCollectionSidebar'
import { ExplorationWorksheet } from '../wordCollection/ExplorationWorksheet'
import { useWorksheetState } from '../../hooks/useWorksheetState'
import { parseSections, getSectionAtLine } from '../../utils/sectionUtils'
import { getWordCount } from '../../utils/textFormatting'
import { type AutoSaveStatus } from '../editor/AutoSaveIndicator'
import { AIFeedbackOverlay, type AIFeedbackItem } from '../debug'
import { useAnalyzerHealth } from './hooks/useAnalyzerHealth'
import { useWordCollectionAIFeedback } from '../../hooks/useWordCollectionAIFeedback'
import { useSessionHealth } from '../../hooks/useSessionHealth'
// PointOfViewModal removed - POV settings now handled by full settings panel

interface CleanSongEditorProps {
  songId: string
  song?: Song  // Optional: Allows parent to push metadata updates for state sync
  onSongChange?: (song: Song) => void
  onSongLoaded?: (song: Song) => void
  onSongUpdate?: (updates: Partial<Song>) => void
  onClose?: () => void
  onSaveStatusChange?: (status: 'saved' | 'saving' | 'error') => void
  onAutoSaveStatusChange?: (status: 'saved' | 'saving' | 'pending' | 'error') => void
  onHasUnsavedChangesChange?: (hasChanges: boolean) => void
}

export interface CleanSongEditorRef {
  triggerSave: () => Promise<void>
}

/**
 * Helper: Extract plain text from Lexical JSON for legacy lyrics field
 *
 * Opening move: Parse Lexical JSON and extract text content
 * Main play: Walk the node tree and collect text from text/stressed-text nodes
 * Victory lap: Return newline-separated text for backwards compatibility
 */
interface LexicalNode {
  type: string
  text?: string
  children?: LexicalNode[]
}

interface LexicalRoot {
  root?: {
    children?: LexicalNode[]
  }
}

function extractPlainTextFromLexical(lexicalJSON: string): string {
  try {
    const parsed: LexicalRoot = JSON.parse(lexicalJSON)
    if (!parsed.root?.children) return ''

    const lines: string[] = []

    // Recursively extract text from any node
    const extractTextFromNode = (node: LexicalNode): string => {
      // Primary: 'lyrics-text' - our LyricsTextNode (See LyricsTextNode.getType() - must NOT be 'text')
      // Fallback: 'text' for edge cases (pasted content), 'stressed-text' for legacy
      if (node.type === 'lyrics-text' || node.type === 'text' || node.type === 'stressed-text') {
        return node.text || ''
      }

      // If it has children, recursively extract from them
      if (node.children && Array.isArray(node.children)) {
        return node.children.map(extractTextFromNode).join('')
      }

      return ''
    }

    // Process each top-level node (paragraphs, section-blocks, etc.)
    const processNode = (node: LexicalNode): void => {
      // Handle section-block nodes - they contain paragraphs
      if (node.type === 'section-block' && node.children) {
        node.children.forEach(processNode)
        return
      }

      // Handle paragraph-like nodes (paragraph, section-paragraph)
      if (node.children) {
        const lineText = extractTextFromNode(node)
        if (lineText.trim()) {
          lines.push(lineText)
        }
      }
    }

    parsed.root.children.forEach(processNode)
    return lines.join('\n')
  } catch (error) {
    console.error('Failed to extract plain text from Lexical:', error)
    return ''
  }
}

/**
 * Convert stability level (1-10) to rhythmicStability (0-1).
 * Level 1 (varied/loose) → 0.0, Level 10 (rigid/stable) → 1.0
 * Matches UI labels: 1="Varied", 10="Rigid"
 */
function convertStabilityLevelToRhythmic(level: number | undefined): number | undefined {
  if (level === undefined) return undefined
  // Clamp to valid range just in case
  const clamped = Math.max(1, Math.min(10, level))
  return (clamped - 1) / 9
}

/**
 * Clean Song Editor following Lexical best practices:
 *
 * 1. ✅ Lexical as single source of truth for editor content
 * 2. ✅ OnChangePlugin pattern with useRef for state capture
 * 3. ✅ Direct API calls without Redux caching interference
 * 4. ✅ No controlled component pattern - no setEditorState during typing
 * 5. ✅ Serialize to JSON only for persistence, not for UI state sync
 */
const CleanSongEditor = forwardRef<CleanSongEditorRef, CleanSongEditorProps>(
  function CleanSongEditor({
    songId,
    song: externalSong,  // Renamed to avoid conflict with internal state
    onSongChange,
    onSongLoaded,
    onSongUpdate,
    onClose,
    onSaveStatusChange,
    onAutoSaveStatusChange,
    onHasUnsavedChangesChange
  }, ref) {

    // UI-only state (no editor content state)
    const [song, setSong] = useState<Song | null>(null)
    // POV Settings now handled by full settings panel
    const [title, setTitle] = useState('')
    const [artist, setArtist] = useState('')
    const [status, setStatus] = useState<Song['status']>('draft')
    const [tags, setTags] = useState<string[]>([])
    const [enableStressMarking, setEnableStressMarking] = useState(false)
    const [initialLyrics, setInitialLyrics] = useState<string>('')  // State for initial editor content

    // Epic 05 Scope 02 Fix 1: Check analyzer health to gate stress features
    const { healthy: analyzerHealthy, loading: analyzerLoading, error: analyzerError } = useAnalyzerHealth()
    // Big play: Only enable stress marking if setting is true AND analyzer is healthy
    const effectiveEnableStress = enableStressMarking && analyzerHealthy

    // Proactive session health monitoring - catches expiry BEFORE user tries to save
    const [showSessionExpiredOverlay, setShowSessionExpiredOverlay] = useState(false)
    // Ref declared early so onSessionExpired callback can reference it
    const sessionExpiredRef = useRef(false)  // Stops auto-save loop when session expires
    // Side-effect only hook: starts session health polling on mount
    // We don't need the return value, just the polling + callback behavior
    useSessionHealth({
      pollIntervalMs: 30000, // Check every 30 seconds
      enabled: true,
      onSessionExpired: () => {
        // Stop auto-save loop (prevents infinite 401 retry)
        sessionExpiredRef.current = true
        // Show overlay immediately - gives user visual feedback before redirect
        setShowSessionExpiredOverlay(true)
      }
    })

    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

    // Section sidebar removed for cleaner interface
    const [sections, setSections] = useState<ReturnType<typeof parseSections>>([])
    const [currentSection, setCurrentSection] = useState<string>('')
    const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('saved')

    // POV Settings save handler removed - now handled by full settings panel
    // const [isProcessingStress, setIsProcessingStress] = useState(false) // TODO: Implement processing state UI

    // Word Collection AI feedback integration
    const { feedback: wordCollectionFeedback, dismiss: dismissWordCollectionFeedback } = useWordCollectionAIFeedback()

    // AI Debug Framework state
    const [aiFeedback, _setAiFeedback] = useState<AIFeedbackItem[]>([])
    const [showAiFeedback, setShowAiFeedback] = useState(false)
    const [highlightedLine, setHighlightedLine] = useState<number | null>(null)
    void highlightedLine // Suppress unused warning until line highlighting is implemented

    // Merge word collection feedback with other feedback
    const allFeedback: AIFeedbackItem[] = [
      ...aiFeedback,
      ...(wordCollectionFeedback ? [wordCollectionFeedback] : [])
    ]

    // Worksheet state
    const {
      isOpen: worksheetOpen,
      searchTerm: worksheetSearchTerm,
      activeTab: worksheetActiveTab,
      parentWordId: worksheetParentWordId,
      searchNonce: worksheetSearchNonce,
      openWorksheet,
      closeWorksheet,
      setSearchTerm: setWorksheetSearchTerm,
      setActiveTab: setWorksheetActiveTab
    } = useWorksheetState()

    // Lexical content captured via OnChangePlugin (Lexical best practice)
    const lexicalContentRef = useRef<string>('')  // Stores the latest editor content
    const plainTextRef = useRef<string>('')      // Plain text for word counts and sections
    const wysiwygEditorRef = useRef<RichTextLyricsEditorRef>(null)
    const isMountedRef = useRef(true)
    const handleSaveRef = useRef<(() => Promise<void>) | null>(null)
    const lastAutoSaveRef = useRef<string>('')
    const savedCanonicalJSONRef = useRef<string>('')  // Baseline: canonical JSON after save/load for change detection
    const isInitialized = useRef(false)
    const prevSongRef = useRef(song)  // FIX (iteration_01_a): Track previous song object for metadata change detection
    const stressProcessedForSongRef = useRef<string | null>(null)  // FIX: Only process stress once per song load
    const onCloseRef = useRef(onClose)  // FIX: Ref for onClose to avoid re-running loadSong effect
    const justSavedRef = useRef(false)  // FIX (Bug #2): Guard against post-save sync triggering dirty

    // Keep onCloseRef updated with latest callback value
    useEffect(() => {
      onCloseRef.current = onClose
    }, [onClose])

    // Debug logging only when needed
    if (typeof window !== 'undefined' && window.location.search.includes('debug=editor')) {
      console.log('🔍 CleanSongEditor render:', {
        hasUnsavedChanges,
        autoSaveStatus,
        isInitialized: isInitialized.current,
        songId
      })
    }

    // Helper function to check if lyrics need stress processing and trigger auto-save
    const checkAndProcessStresses = useCallback(async (lyrics: string) => {
      if (!lyrics || !wysiwygEditorRef.current) {
        return
      }

      try {
        // Check if the lyrics contain Lexical JSON with existing stress nodes
        let needsStressProcessing = false

        if (lyrics.startsWith('{')) {
          // Lexical JSON format - check if it has StressedTextNode instances
          try {
            const lexicalData = JSON.parse(lyrics)
            const hasStressNodes = JSON.stringify(lexicalData).includes('"type":"stressed-text"')
            needsStressProcessing = !hasStressNodes
          } catch {
            // Invalid JSON, treat as plain text
            needsStressProcessing = true
          }
        } else {
          // Plain text format - always needs stress processing
          needsStressProcessing = true
        }

        if (needsStressProcessing) {
          // Wait for the AutoStressDetectionPlugin to process, then trigger auto-save
          setTimeout(async () => {
            try {
              if (handleSaveRef.current) {
                await handleSaveRef.current()
              }
            } catch (error) {
              console.error('Auto-save after stress processing failed:', error)
            }
          }, 800)
        }
      } catch (error) {
        console.error('Stress check error:', error)
      }
    }, [])

    // Set mounted state
    useEffect(() => {
      isMountedRef.current = true
      return () => {
        isMountedRef.current = false
      }
    }, [])

    // Load song data using direct fetch (no Redux/RTK Query caching) with proper auth headers
    useEffect(() => {
      const loadSong = async () => {
        setIsLoading(true)
        setError(null)

        // Reset save-related state at start of new song load
        // This ensures no state leakage from previous songs
        setHasUnsavedChanges(false)
        setAutoSaveStatus('saved')
        setIsSaving(false)
        isInitialized.current = false
        stressProcessedForSongRef.current = null
        savedCanonicalJSONRef.current = ''
        lastAutoSaveRef.current = ''

        // Notify parent of reset save state at start of load
        if (onHasUnsavedChangesChange) {
          onHasUnsavedChangesChange(false)
        }
        if (onAutoSaveStatusChange) {
          onAutoSaveStatusChange('saved')
        }

        try {
          // Get auth headers from backend auth service
          const { data: { session } } = await authService.getSession()
          const headers: HeadersInit = {
            'Content-Type': 'application/json',
          }

          if (session?.access_token) {
            headers['Authorization'] = `Bearer ${session.access_token}`
          }

          const response = await fetch(`${API_BASE_URL}/api/songs/${songId}`, {
            headers
          })

          if (!response.ok) {
            if (response.status === 401) {
              // Session expired - stop any further operations and handle it
              sessionExpiredRef.current = true
              console.warn('🚫 401 Unauthorized: Session expired while loading song', songId)
              await authService.handleSessionExpired({ view: 'editor', songId })
              return
            }
            if (response.status === 403) {
              // User doesn't have permission to access this song - redirect to song list
              console.warn('🚫 403 Forbidden: User cannot access song', songId)
              if (onCloseRef.current) {
                setTimeout(() => onCloseRef.current?.(), 100) // Redirect back to song list
                return
              }
            }
            throw new Error(`Failed to load song: ${response.statusText}`)
          }

          const songData = await response.json()
          const actualSong = songData.song || songData

          // Debug: Song loaded
          // console.log('📂 Song loaded via direct fetch:', {
          //   id: actualSong.id,
          //   title: actualSong.title,
          //   lyricsLength: actualSong.lyrics?.length || 0,
          //   hasSettings: !!actualSong.settings
          // })

          // Set all metadata state at once
          setSong(actualSong)
          setTitle(actualSong.title || '')
          setArtist(actualSong.artist || '')
          setStatus(actualSong.status || 'draft')
          setTags(actualSong.tags || [])
          setEnableStressMarking(actualSong.settings?.prosody_settings?.syllable_emphasis || false)
          // POV settings now handled by full settings panel
          setError(null)

          // Opening move: load from lexical_content if available, otherwise use legacy lyrics
          let loadedLyrics = actualSong.lyrics || ''

          if (actualSong.lexical_content) {
            // Use native Lexical JSON directly
            loadedLyrics = typeof actualSong.lexical_content === 'string'
              ? actualSong.lexical_content
              : JSON.stringify(actualSong.lexical_content)
          }
          // Else: use plain text lyrics (will be converted to Lexical on first save)

          // Initialize Lexical content
          setInitialLyrics(loadedLyrics)
          lexicalContentRef.current = loadedLyrics
          plainTextRef.current = loadedLyrics

          // Notify parent of song load with enriched lyrics data
          // ALWAYS derive lyrics from lexical_content when available
          // This ensures SongSettingsPanel has accurate lyrics regardless of backend state
          if (onSongLoaded) {
            // Always extract from lexical_content if we have it - don't rely on backend lyrics field
            const enrichedLyrics = actualSong.lexical_content
              ? extractPlainTextFromLexical(loadedLyrics)
              : actualSong.lyrics || ''
            const songWithLyrics = { ...actualSong, lyrics: enrichedLyrics }
            setTimeout(() => {
              onSongLoaded(songWithLyrics)
            }, 0)
          }

          // Keep loading state active during full initialization
          setTimeout(() => {
            isInitialized.current = true

            // FIX (Bug #1): Set baseline IMMEDIATELY when initialization completes
            // Previously, baseline was set on first handleLexicalContentChange callback,
            // but Lexical doesn't fire onChange during JSON initialization.
            // If user edits before any onChange fires, baseline was set to modified state.
            // Solution: Use the loaded content as baseline - it matches what editor initialized with.
            if (savedCanonicalJSONRef.current === '') {
              savedCanonicalJSONRef.current = lexicalContentRef.current
              lastAutoSaveRef.current = lexicalContentRef.current
            }

            // Post-load processing will be triggered by editor ready event

            // End loading state after a brief delay (editor ready will handle stress processing)
            setTimeout(() => {
              setIsLoading(false)
            }, 1000) // Shorter delay since we're not waiting for stress processing
          }, 1000) // Minimum loading time so user can see the loading state

        } catch (err) {
          console.error('Failed to load song:', err)
          const errorMessage = err instanceof Error ? err.message : 'Failed to load song'

          // Handle authentication errors by redirecting to song list
          if (errorMessage.includes('403') || errorMessage.includes('Forbidden') || errorMessage.includes('Session expired')) {
            console.warn('🚫 Authentication error: User cannot access song', songId)
            if (onCloseRef.current) {
              setTimeout(() => onCloseRef.current?.(), 100) // Redirect back to song list
              return
            }
          }

          setError(errorMessage)
          setIsLoading(false) // Only end loading on error
        }
      }

      loadSong()
      // Note: onClose is accessed via onCloseRef to avoid re-running effect when handleCloseSong changes
    }, [songId, onSongLoaded, onHasUnsavedChangesChange, onAutoSaveStatusChange])

    // Set up editor ready listener for stress processing - runs ONCE per song load
    useEffect(() => {
      if (!wysiwygEditorRef.current || !song) return

      // FIX: Only process stress once per song load, not on every song object update
      // The song object changes after saves (server response), but we don't need to re-check stress
      if (stressProcessedForSongRef.current === songId) return
      stressProcessedForSongRef.current = songId

      const unregister = wysiwygEditorRef.current.onEditorReady(async () => {
        await checkAndProcessStresses(song.lyrics || '')
      })

      return () => {
        unregister()
      }
    }, [song, songId, checkAndProcessStresses])

    // Track metadata changes (non-content fields)
    // FIX: Trigger immediate save for metadata changes instead of waiting for auto-save timer
    useEffect(() => {
      if (!song) return

      // FIX #3 (iteration_01_b): Update prevSongRef when editor first initializes (baseline)
      if (!isInitialized.current) {
        prevSongRef.current = song
        return
      }


      // FIX #2 (iteration_01_b): Guard against null/undefined prevSongRef before accessing properties
      // FIX (iteration_01_a): Compare against previous song object instead of current props
      // This catches changes BEFORE both values update simultaneously
      const hasMetadataChanges = (
        prevSongRef.current &&
        (
          prevSongRef.current.title !== song.title ||
          prevSongRef.current.artist !== (song.artist || '') ||
          prevSongRef.current.status !== song.status ||
          JSON.stringify(prevSongRef.current.tags) !== JSON.stringify(song.tags)
        )
      )

      if (hasMetadataChanges && prevSongRef.current) {
        // FIX (Bug #2 - endless loop): Skip marking dirty if we just saved
        // The post-save sync from parent will trigger this effect, but it's not a user change
        if (justSavedRef.current) {
          prevSongRef.current = song  // Still update ref to keep in sync
          return
        }

        setHasUnsavedChanges(true)
        setAutoSaveStatus('pending')

        if (onHasUnsavedChangesChange) {
          onHasUnsavedChangesChange(true)
        }
        if (onAutoSaveStatusChange) {
          onAutoSaveStatusChange('pending')
        }

        // Trigger immediate save for metadata (don't wait 10 seconds)
        // Use handleSaveRef pattern (established pattern in this component)
        const saveTimer = setTimeout(() => {
          // Guard against race condition where handleSaveRef isn't ready yet
          if (isMountedRef.current && !isSaving && handleSaveRef.current) {
            handleSaveRef.current()
          } else if (!handleSaveRef.current) {
            // If handleSaveRef isn't ready, retry after a short delay
            setTimeout(() => {
              if (handleSaveRef.current) {
                handleSaveRef.current()
              } else {
                console.error('Metadata save failed - handleSaveRef never initialized')
                setAutoSaveStatus('error')
              }
            }, 100)
          }
        }, 100) // Small delay to batch rapid metadata changes

        // FIX #1 (iteration_01_b): Update prevSongRef ONLY when metadata actually changed
        // This must be INSIDE the conditional to avoid defeating the comparison logic
        prevSongRef.current = song

        return () => clearTimeout(saveTimer)
      }
    }, [song, title, artist, status, tags, onHasUnsavedChangesChange, onAutoSaveStatusChange, isSaving])

    // React to stress marking settings changes
    useEffect(() => {
      if (song?.settings?.prosody_settings?.syllable_emphasis !== undefined) {
        setEnableStressMarking(song.settings.prosody_settings.syllable_emphasis)
      }
    }, [song?.settings?.prosody_settings?.syllable_emphasis])

    // Scope 21 Fix #2: Mirror rhyme_schemes into metadata for persistence
    // This ensures rhyme scheme settings are available when song is reopened
    useEffect(() => {
      if (!song || !isInitialized.current) return

      // Check if rhyme_schemes exists in settings but not in metadata
      const hasRhymeSchemes = song.settings?.rhyme_schemes
      const metadataRhymeSchemes = song.metadata?.rhymeSchemes

      // Sync to metadata if missing or different
      if (hasRhymeSchemes && JSON.stringify(hasRhymeSchemes) !== JSON.stringify(metadataRhymeSchemes)) {
        const updatedSong = {
          ...song,
          metadata: {
            ...song.metadata,
            rhymeSchemes: song.settings.rhyme_schemes
          }
        }

        setSong(updatedSong)
        onSongUpdate?.(updatedSong)
      }
    }, [song, onSongUpdate])

    // FIX (iteration_01_c): Sync internal metadata state when parent updates song via prop
    // This ensures metadata save effect can detect changes by comparing against current values
    // FIX (iteration_01_a): Also sync settings changes so prosody flags propagate to editor
    useEffect(() => {
      // Don't sync if no external song provided
      if (!externalSong) return

      // Don't sync on initial load - let loadSong effect handle it
      if (!isInitialized.current) return

      // Don't sync if it's a different song entirely
      if (song && externalSong.id !== song.id) return

      // Check if metadata actually changed
      const metadataChanged = (
        externalSong.title !== title ||
        externalSong.artist !== artist ||
        externalSong.status !== status ||
        JSON.stringify(externalSong.tags) !== JSON.stringify(tags)
      )

      // Opening move: Also check if settings changed (prosody, narrative, etc.)
      // This is how the settings modal pushes changes into the editor
      const settingsChanged = song && (
        JSON.stringify(externalSong.settings) !== JSON.stringify(song.settings)
      )

      if (metadataChanged || settingsChanged) {
        // Update internal state to match parent
        // Opening move: Update the main song object (includes settings!)
        // This triggers the stress marking effect which watches song.settings
        setSong(externalSong)

        // Main play: Update individual metadata fields
        // These are the fields that the metadata save effect monitors
        if (metadataChanged) {
          setTitle(externalSong.title || '')
          setArtist(externalSong.artist || '')
          setStatus(externalSong.status || 'draft')
          setTags(externalSong.tags || [])
        }

      }
    }, [externalSong, song, title, artist, status, tags])

    // Save song using direct API call
    const handleSave = useCallback(async () => {
      if (!isMountedRef.current || !song) return

      // Don't attempt save if session already expired - prevents infinite loop
      if (sessionExpiredRef.current) {
        console.warn('🚫 Save blocked: session already expired')
        return
      }

      // Check if there are actual changes (excluding POV settings - handled separately)
      // FIX: Compare lexical content by parsing JSON to handle different key ordering
      // JSON.stringify doesn't guarantee key order, so {"a":1,"b":2} !== {"b":2,"a":1} as strings
      // Solution: Parse both to objects, then stringify through the SAME code path
      let lexicalMatch = false
      try {
        const currentParsed = JSON.parse(lexicalContentRef.current || '{}')
        const savedContent = song.lexical_content
        const savedParsed = typeof savedContent === 'string' ? JSON.parse(savedContent) : savedContent
        // Stringify both through the same path - this normalizes key order
        const normalizedCurrent = JSON.stringify(currentParsed)
        const normalizedSaved = JSON.stringify(savedParsed)
        lexicalMatch = normalizedCurrent === normalizedSaved
      } catch {
        // If parsing fails, fall back to string comparison
        const savedLexicalContent = song.lexical_content
          ? (typeof song.lexical_content === 'string' ? song.lexical_content : JSON.stringify(song.lexical_content))
          : ''
        lexicalMatch = lexicalContentRef.current === savedLexicalContent
      }

      const hasActualChanges = (
        title !== song.title ||
        artist !== (song.artist || '') ||
        !lexicalMatch ||
        status !== song.status ||
        JSON.stringify(tags) !== JSON.stringify(song.tags)
      )

      if (!hasActualChanges) {
        if (isMountedRef.current) {
          setTimeout(() => setHasUnsavedChanges(false), 0)
        }
        return
      }

      // Only set isSaving if it's not already true (manual save may have set it)
      if (!isSaving) {
        setIsSaving(true)
      }
      setError(null)
      onSaveStatusChange?.('saving')

      try {
        // Opening move: Get current content - use ref as primary, editor as fallback
        let currentLexicalContent = lexicalContentRef.current

        // CANONICAL SERIALIZATION: Use editorState.toJSON() for deterministic output
        // This matches the OnChangePlugin callback which uses the same canonical format
        // Never use JSON.stringify(editorState) directly - it doesn't produce canonical JSON
        if (wysiwygEditorRef.current) {
          const editorElement = wysiwygEditorRef.current.getWysiwygElement()
          if (editorElement && (editorElement as unknown as { __lexicalEditor?: unknown }).__lexicalEditor) {
            const editor = (editorElement as unknown as { __lexicalEditor: { getEditorState: () => { toJSON: () => unknown } } }).__lexicalEditor
            // CRITICAL: Use .toJSON() for canonical serialization (Epic 09 Fix #2)
            const freshContent = JSON.stringify(editor.getEditorState().toJSON())
            if (freshContent !== currentLexicalContent) {
              console.warn('⚠️ Using fresh editor state (ref was stale)')
              currentLexicalContent = freshContent
            }
          }
        }

        // Big play: save Lexical JSON directly (no conversion!)
        const lexicalContentObject = JSON.parse(currentLexicalContent)

        // Generate plain text for backwards compatibility
        const plainTextLyrics = extractPlainTextFromLexical(currentLexicalContent)

        const updateData = {
          title,
          artist: artist || undefined,
          lyrics: plainTextLyrics, // Legacy text field for compatibility
          lexical_content: lexicalContentObject, // Native Lexical EditorState JSON
          status,
          tags,
          // POV settings handled separately via manual save
        }

        // Get auth headers from backend auth service
        const { data: { session } } = await authService.getSession()
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        }

        if (session?.access_token) {
          headers['Authorization'] = `Bearer ${session.access_token}`
        }

        const response = await fetch(`${API_BASE_URL}/api/songs/${songId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(updateData)
        })

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            // Session expired - stop the loop and handle it
            sessionExpiredRef.current = true
            console.warn(`🚫 ${response.status}: Session expired while saving song`, songId)
            await authService.handleSessionExpired({ view: 'editor', songId })
            throw new Error('Session expired. Please log in again.')
          }
          throw new Error(`Save failed: ${response.statusText}`)
        }

        const updatedSong = await response.json()

        if (!isMountedRef.current) return

        // Update song state with response (but don't update editor content)
        const responseData = updatedSong.song || updatedSong
        setSong(responseData)

        // Update local state to match what was saved
        setTitle(responseData.title || title)
        setArtist(responseData.artist || artist || '')
        setStatus(responseData.status || status)
        setTags(responseData.tags || tags)

        // Update saved baseline after successful save
        savedCanonicalJSONRef.current = lexicalContentRef.current

        // FIX (Bug #2 - endless loop): Update prevSongRef to server response
        // This prevents the metadata tracking effect from detecting false "changes"
        // when comparing old song state vs server response after save
        prevSongRef.current = responseData

        // FIX (Bug #2 - endless loop): Set guard flag to prevent post-save sync from triggering dirty
        // The parent (App.tsx) will receive the song update and sync it back, which triggers effects.
        // We need to ignore dirty detection during this sync window.
        justSavedRef.current = true
        setTimeout(() => {
          justSavedRef.current = false
        }, 500) // 500ms window should be plenty for sync to complete

        setHasUnsavedChanges(false)
        setAutoSaveStatus('saved')

        if (onHasUnsavedChangesChange) {
          onHasUnsavedChangesChange(false)
        }
        if (onAutoSaveStatusChange) {
          onAutoSaveStatusChange('saved')
        }

        setTimeout(() => {
          onSaveStatusChange?.('saved')
          if (onSongChange) {
            onSongChange(responseData)
          }
        }, 0)

      } catch (err) {
        console.error('Save failed:', err)
        if (isMountedRef.current) {
          setError(err instanceof Error ? err.message : 'Failed to save song')
          setAutoSaveStatus('error')
          setTimeout(() => {
            onSaveStatusChange?.('error')
          }, 0)
          if (onAutoSaveStatusChange) {
            onAutoSaveStatusChange('error')
          }
        }
        // Rethrow so triggerSave() callers can handle failure (e.g., block navigation)
        throw err
      } finally {
        if (isMountedRef.current) {
          setIsSaving(false)
        }
      }
    }, [song, songId, title, artist, status, tags, onSongChange, onSaveStatusChange, onHasUnsavedChangesChange, onAutoSaveStatusChange, isSaving])

    // Update save ref
    useEffect(() => {
      handleSaveRef.current = handleSave
    }, [handleSave])

    // Expose triggerSave
    useImperativeHandle(ref, () => ({
      triggerSave: async () => {
        if (handleSaveRef.current) {
          return await handleSaveRef.current()
        }
        return Promise.resolve()
      }
    }), [])

    // Auto-save with Lexical content
    const handleAutoSave = useCallback(async (lexicalContent?: string) => {
      if (!isMountedRef.current || !song || isSaving || !isInitialized.current) {
        return
      }

      const contentToSave = lexicalContent || lexicalContentRef.current

      // Auto-save excludes POV settings - they have manual save only
      // Compare lexical content by parsing JSON to handle different key ordering
      let lexicalMatch = false
      try {
        const currentParsed = JSON.parse(contentToSave || '{}')
        const savedContent = song.lexical_content
        const savedParsed = typeof savedContent === 'string' ? JSON.parse(savedContent) : savedContent
        lexicalMatch = JSON.stringify(currentParsed) === JSON.stringify(savedParsed)
      } catch {
        const savedLexicalContent = song.lexical_content
          ? (typeof song.lexical_content === 'string' ? song.lexical_content : JSON.stringify(song.lexical_content))
          : ''
        lexicalMatch = contentToSave === savedLexicalContent
      }

      const hasActualChanges = (
        title !== song.title ||
        artist !== (song.artist || '') ||
        !lexicalMatch ||
        status !== song.status ||
        JSON.stringify(tags) !== JSON.stringify(song.tags)
      )

      if (!hasActualChanges || contentToSave === lastAutoSaveRef.current) {
        setAutoSaveStatus('saved')
        return
      }

      try {
        setAutoSaveStatus('saving')
        lastAutoSaveRef.current = contentToSave

        // Update the ref with current content
        if (lexicalContent) {
          lexicalContentRef.current = lexicalContent
        }

        await handleSave()

        // FIX (Bug #2): Don't duplicate baseline/state updates here
        // handleSave() already sets savedCanonicalJSONRef and hasUnsavedChanges
        // Setting them again with potentially stale `contentToSave` value caused
        // inconsistency that triggered the endless loop.
        // Only update auto-save-specific status here.
        if (isMountedRef.current) {
          setAutoSaveStatus('saved')
        }
      } catch (error) {
        console.error('Auto-save failed:', error)
        if (isMountedRef.current) {
          setAutoSaveStatus('error')
        }
      }
    }, [song, isSaving, title, artist, status, tags, handleSave])

    // Auto-save timer for unsaved changes
    useEffect(() => {
      // Don't schedule auto-save if session expired - prevents infinite loop
      if (!hasUnsavedChanges || !isInitialized.current || isSaving || sessionExpiredRef.current) {
        return
      }

      const autoSaveTimer = setTimeout(() => {
        // Double-check session hasn't expired since timer was scheduled
        if (hasUnsavedChanges && !isSaving && isMountedRef.current && !sessionExpiredRef.current) {
          handleAutoSave()
        }
      }, 10000) // 10 seconds

      return () => {
        clearTimeout(autoSaveTimer)
      }
    }, [hasUnsavedChanges, handleAutoSave, isSaving])

    // Auto-save status sync - simpler logic to avoid conflicts
    useEffect(() => {
      if (!hasUnsavedChanges && autoSaveStatus !== 'saving' && autoSaveStatus !== 'saved') {
        setAutoSaveStatus('saved')
      }
    }, [hasUnsavedChanges, autoSaveStatus])

    useEffect(() => {
      if (onAutoSaveStatusChange) {
        onAutoSaveStatusChange(autoSaveStatus)
      }
    }, [autoSaveStatus, onAutoSaveStatusChange])

    // AI Debug Framework handlers
    // Note: handleAiAnalysisResult disabled - AI debug button removed from UI
    // const handleAiAnalysisResult = useCallback((feedback: AIFeedbackItem[]) => {
    //   setAiFeedback(feedback)
    //   setShowAiFeedback(true)
    // }, [])

    const handleLineHighlight = useCallback((lineNumber: number) => {
      setHighlightedLine(lineNumber)
      // TODO: Integrate with Lexical editor to actually highlight the line
      console.log('🎯 Highlighting line:', lineNumber)
    }, [])

    // Handle plain text changes for UI updates (sections, word count)
    const handlePlainTextChange = useCallback((plainText: string) => {
      // Debug logging only when enabled
      if (typeof window !== 'undefined' && window.location.search.includes('debug=callbacks')) {
        console.log('🔍 CALLBACK: handlePlainTextChange called with:', {
          length: plainText?.length || 0,
          isInitialized: isInitialized.current,
          preview: (plainText || '').substring(0, 30) + '...',
          songLyricsLength: song?.lyrics?.length || 0,
          contentMatches: plainText === song?.lyrics
        })
      }

      // Guard: Skip onChange during initialization or if content hasn't stabilized yet
      if (!isInitialized.current) return

      // Guard: Skip spurious onChange calls with partial content during editor mount
      // If we just loaded lyrics and the change is drastically shorter, it's likely
      // the editor still initializing its DOM - ignore to prevent false dirty state
      if (song?.lyrics && plainText.length < song.lyrics.length * 0.5) {
        return
      }

      // Store plain text for word counts and section parsing
      plainTextRef.current = plainText || ''

      // Parse sections from plain text
      const parsedSections = parseSections(plainText || '')
      setSections(parsedSections)

      if (currentSection && !parsedSections.find(s => s.name === currentSection)) {
        setCurrentSection('')
      }

      // REMOVED: Plain text change detection (architectural simplification)
      // Reason: Plain text is for display/parsing only, NOT change detection
      // - Songs loaded from lexical_content have empty song.lyrics field
      // - Comparing plainText vs song.lyrics always triggers false positive
      // - Lexical JSON comparison (handleLexicalContentChange) is authoritative
      // - Legacy plain text songs convert to Lexical JSON on first edit anyway
      // Change detection now handled ONLY by handleLexicalContentChange callback
    }, [currentSection, song])

    // Handle text content changes for word count and section parsing only
    // Change detection is handled by handleLexicalContentChange using canonical JSON
    const handleTextContentChange = useCallback((textContent: string) => {
      // Store for word count display - parsing happens in handlePlainTextChange
      plainTextRef.current = textContent
    }, [])

    // Handle Lexical content changes using canonical JSON comparison
    // This is the authoritative change detection mechanism
    //
    // ARCHITECTURE NOTE (Epic 09 iteration_02_b):
    // - OnChangePlugin has ignoreSelectionChange={true} to prevent false triggers
    // - This callback fires when content changes (dirtyElements > 0 or dirtyLeaves > 0)
    // - We compare canonical JSON strings (editorState.toJSON())
    // - Baseline is now set during song load initialization (Bug #1 fix)
    // - This callback updates lexicalContentRef on every call (even before init)
    //   so that the baseline set during init has the editor's canonical representation
    const handleLexicalContentChange = useCallback((lexicalJSON: string) => {
      // Always update ref for save operation AND for baseline setting during init
      // This runs even before isInitialized=true, so lexicalContentRef has canonical JSON
      lexicalContentRef.current = lexicalJSON

      // Skip change detection during loading
      if (!isInitialized.current) {
        return
      }

      // FALLBACK: If baseline wasn't set during init (shouldn't happen), set it now
      // This handles edge cases like hot reloads or unusual initialization sequences
      if (savedCanonicalJSONRef.current === '') {
        savedCanonicalJSONRef.current = lexicalJSON
        lastAutoSaveRef.current = lexicalJSON
        return // Don't mark as dirty - this is initialization
      }

      // Simple canonical JSON comparison: current vs saved
      const isDirty = lexicalJSON !== savedCanonicalJSONRef.current

      // FIX (Bug #2 - endless loop): Skip dirty detection during post-save sync window
      if (justSavedRef.current && isDirty) {
        return
      }

      // Use ref to check current dirty state to avoid stale closure issues
      // We read hasUnsavedChanges via closure but this is safe since we only
      // need it for comparison to avoid duplicate state updates
      setHasUnsavedChanges(prev => {
        if (isDirty !== prev) {
          // Notify parent outside setState to avoid batching issues
          setTimeout(() => onHasUnsavedChangesChange?.(isDirty), 0)
          return isDirty
        }
        return prev
      })
    }, [onHasUnsavedChangesChange])

    // Section management functions
    const updateCurrentSection = useCallback(() => {
      if (sections.length === 0) {
        if (currentSection !== '') {
          setCurrentSection('')
        }
        return
      }

      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0) {
        return
      }

      try {
        const editorElement = wysiwygEditorRef.current?.getWysiwygElement()
        if (!editorElement) return

        let currentLineNumber = 0
        const range = selection.getRangeAt(0)

        // Try to find the prosody line element containing the cursor
        let targetNode: Node | null = range.startContainer
        let prosodyLine: HTMLElement | null = null

        while (targetNode && targetNode !== editorElement) {
          if (targetNode instanceof HTMLElement && targetNode.classList.contains('prosody-line')) {
            prosodyLine = targetNode
            break
          }
          if (targetNode instanceof HTMLElement && targetNode.hasAttribute('data-line')) {
            prosodyLine = targetNode
            break
          }
          targetNode = targetNode.parentNode
        }

        if (prosodyLine && prosodyLine.hasAttribute('data-line')) {
          currentLineNumber = parseInt(prosodyLine.getAttribute('data-line') || '0')
        } else {
          // Fallback: calculate line number from text content
          const preCaretRange = range.cloneRange()
          preCaretRange.selectNodeContents(editorElement)
          preCaretRange.setEnd(range.endContainer, range.endOffset)
          const textBeforeCursor = preCaretRange.toString()
          currentLineNumber = textBeforeCursor.split('\n').length - 1
        }

        const sectionAtCursor = getSectionAtLine(sections, currentLineNumber)
        const newCurrentSection = sectionAtCursor?.name || ''

        if (newCurrentSection !== currentSection) {
          setCurrentSection(newCurrentSection)
        }
      } catch (error) {
        console.debug('Section detection error:', error)
      }
    }, [sections, currentSection])

    // TODO: Deprecated, remove
    // Section manipulation functions
    // const handleInsertSection = useCallback((sectionType: SectionType) => {
    //   const editorRef = wysiwygEditorRef.current
    //   if (!editorRef) {
    //     const sectionTag = generateSectionTag(sections, sectionType)
    //     const newLyrics = plainTextRef.current + (plainTextRef.current ? '\n\n' : '') + sectionTag + '\n'
    //     const renumbered = renumberSections(newLyrics)
    //     plainTextRef.current = renumbered
    //     updateCurrentSection()
    //     return
    //   }
    //
    //   try {
    //     const cursorPosition = editorRef.getCurrentCursorPosition()
    //     const selectedText = editorRef.getSelectedText()
    //     const sectionTag = generateSectionTag(sections, sectionType)
    //
    //     let newLyrics: string
    //     let newCursorPosition: number = cursorPosition || 0
    //
    //     if (selectedText && selectedText.trim()) {
    //       const startPosition = cursorPosition || 0
    //       const endPosition = startPosition + selectedText.length
    //       const result = wrapTextWithSection(plainTextRef.current, startPosition, endPosition, sectionTag)
    //       newLyrics = result.newLyrics
    //       newCursorPosition = result.newEndPosition
    //     } else {
    //       const result = insertSectionAtPosition(plainTextRef.current, cursorPosition || 0, sectionTag)
    //       newLyrics = result.newLyrics
    //       newCursorPosition = result.newPosition
    //     }
    //
    //     const renumbered = renumberSections(newLyrics)
    //     plainTextRef.current = renumbered
    //
    //     setTimeout(() => {
    //       editorRef.setCursorPosition(newCursorPosition)
    //       updateCurrentSection()
    //     }, 0)
    //
    //   } catch (error) {
    //     console.error('Error inserting section:', error)
    //   }
    // }, [sections, updateCurrentSection])

    // Note: handleAddSection removed as functionality is now handled by toolbar
    // Keeping handleInsertSection for programmatic section insertion

    // TODO: Deprecated, remove
    // Additional toolbar handlers
    // const handleRenumberSections = useCallback(() => {
    //   const renumbered = renumberSections(plainTextRef.current)
    //   if (renumbered !== plainTextRef.current) {
    //     plainTextRef.current = renumbered
    //     // Update Lexical editor content
    //     const editorRef = wysiwygEditorRef.current
    //     if (editorRef) {
    //       editorRef.setContent(renumbered)
    //     }
    //     updateCurrentSection()
    //   }
    // }, [updateCurrentSection])

    // TODO: Deprecated, remove
    // const handleClearSections = useCallback(() => {
    //   if (window.confirm('Are you sure you want to clear all section tags? This cannot be undone.')) {
    //     // Remove all section tags but keep the lyrics text
    //     const clearedLyrics = plainTextRef.current.replace(/^\[.*?\]$/gm, '').replace(/\n\n+/g, '\n\n').trim()
    //     plainTextRef.current = clearedLyrics
    //
    //     // Update Lexical editor content
    //     const editorRef = wysiwygEditorRef.current
    //     if (editorRef) {
    //       editorRef.setContent(clearedLyrics)
    //     }
    //     updateCurrentSection()
    //   }
    // }, [updateCurrentSection])

    // Note: handleDeleteSection and handleRenameSection removed
    // Note: handleSettingChange removed - add back when sidebar settings integration is needed
    // Section management is now handled through the toolbar UI
    // Keeping updateCurrentSection and handleInsertSection for core functionality

    if (isLoading) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
          <div className="text-center">
            <div className="relative mb-6">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-white/20 border-t-blue-400 mx-auto"></div>
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500/20 to-purple-500/20 opacity-40 blur-xl animate-pulse"></div>
            </div>
            <p className="text-lg font-medium text-white animate-pulse">Loading your song...</p>
            <p className="text-sm text-neutral-300 mt-2">Preparing your creative workspace</p>
          </div>
        </div>
      )
    }

    if (error && !song) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-8">
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-red-500/20 to-red-600/20 border border-red-400/30 backdrop-blur-sm flex items-center justify-center">
              <span className="text-2xl">⚠️</span>
            </div>
            <h2 className="text-xl font-bold text-white mb-3">Unable to load song</h2>
            <p className="text-red-300 mb-6 bg-red-500/10 rounded-xl p-4 border border-red-400/30 backdrop-blur-sm">{error}</p>
            <button
              onClick={onClose}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              ← Back to Song List
            </button>
          </div>
        </div>
      )
    }

    return (
      <div className="h-full flex flex-col relative" style={{ background: 'var(--editor-bg-primary)' }}>
        {/* Session Expired Overlay - blocks editing when session dies */}
        {showSessionExpiredOverlay && (
          <div
            data-testid="session-expired-overlay"
            className="absolute inset-0 z-50 flex items-center justify-center"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              backdropFilter: 'blur(4px)',
            }}
          >
            <div
              className="bg-white rounded-lg shadow-xl p-8 max-w-md text-center"
              style={{ margin: '20px' }}
            >
              <div className="text-4xl mb-4">🔒</div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Session Expired
              </h2>
              <p className="text-gray-600 mb-6">
                Your session has timed out. Don't worry — your work is safe.
                Please log in again to continue editing.
              </p>
              <button
                data-testid="session-expired-login-button"
                onClick={() => window.location.href = '/login'}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Go to Login
              </button>
            </div>
          </div>
        )}

        {/* Header removed - now rendered by App.tsx wrapper with AppHeader */}

        {/* Main Layout */}
        <div className="flex-1 flex overflow-hidden">

          <div className="m-4 mr-2">
            <WordCollectionSidebar
              songId={songId}
              onOpenWorksheet={openWorksheet}
            />
          </div>

          {/* Main Editor Content */}
          <div
              className="flex-1 max-w-4xl m-4 mr-2 flex flex-col subtle-shadow"
              style={{
                  background: 'var(--editor-bg-panel)',
                  border: '1px solid var(--editor-border-primary)',
                  borderRadius: '8px'
              }}>

            {/* Editor Content */}
            <div data-testid="lyrics-editor" className="flex-1 relative overflow-hidden">
              {/* Epic 05 Scope 02 Fix 1: Show warning banner if analyzer is unhealthy */}
              {!analyzerLoading && !analyzerHealthy && enableStressMarking && (
                <div
                  data-testid="analyzer-health-warning"
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#fef3c7',
                    borderLeft: '4px solid #f59e0b',
                    color: '#92400e',
                    fontSize: '14px',
                    margin: '8px',
                  }}
                >
                  ⚠️ Stress analysis tools are currently unavailable
                  {analyzerError && `: ${analyzerError}`}
                </div>
              )}
              {!isLoading && song && (
                <RichTextLyricsEditor
                  key={song.id}
                  ref={wysiwygEditorRef}
                  value={initialLyrics}
                  onChange={handlePlainTextChange}
                  onLexicalChange={handleLexicalContentChange}
                  onTextContentChange={handleTextContentChange}
                  onSelectionChange={updateCurrentSection}
                  onStressPatternChange={() => {
                    // Epic 07: Stress pattern changes should trigger save state
                    // Guard: Only block during active save operation (isSaving) or just-saved window
                    // Stress overrides are explicit user actions that should always trigger unsaved state
                    if (isInitialized.current && !hasUnsavedChanges && !isSaving && !justSavedRef.current) {
                      setHasUnsavedChanges(true)
                      setAutoSaveStatus('pending')
                      if (onHasUnsavedChangesChange) {
                        onHasUnsavedChangesChange(true)
                      }
                    }
                  }}
                  placeholder="Enter your lyrics here..."
                  rows={24}
                  enableAutoSave={true}
                  autoSaveDelay={10000}
                  editable={!isLoading}
                  enableStressMarking={effectiveEnableStress}
                  sectionSchemes={song.settings?.rhyme_schemes}
                  rhythmicStability={convertStabilityLevelToRhythmic(song.settings?.prosody_settings?.rhythmic_stability)}
                />
              )}
              {isLoading && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-gray-500">Loading lyrics...</div>
                </div>
              )}

              {/* AI Feedback Overlay moved outside overflow container */}
            </div>

            {/* Editor Status Bar - Simplified: counts only, section chips removed */}
            <div data-testid="editor-status-bar" style={{ borderTop: '1px solid var(--editor-border-primary)', padding: '12px 16px', background: 'var(--editor-bg-secondary)' }}>
              <div className="flex justify-between gap-4 text-sm" style={{ color: 'var(--editor-text-muted)' }}>
                <div className="flex gap-4">
                  <span data-testid="editor-line-count">Lines: {plainTextRef.current.split('\n').length}</span>
                  <span data-testid="editor-word-count">Words: {getWordCount(plainTextRef.current)}</span>
                  <span data-testid="editor-char-count">Characters: {plainTextRef.current.length}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid="ai-analysis-toggle"
                    onClick={() => setShowAiFeedback(!showAiFeedback)}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                      showAiFeedback
                        ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                        : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10 hover:text-white'
                    }`}
                    title={showAiFeedback ? 'Hide AI Analysis panel' : 'Show AI Analysis panel'}
                  >
                    🤖 AI Analysis {showAiFeedback ? '✓' : ''}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Feedback Overlay - Outside overflow container for proper layering */}
        <AIFeedbackOverlay
          feedback={allFeedback}
          isVisible={showAiFeedback}
          onClose={() => {
            setShowAiFeedback(false)
            // Dismiss word collection feedback if present
            if (wordCollectionFeedback) {
              dismissWordCollectionFeedback()
            }
          }}
          onLineHighlight={handleLineHighlight}
        />

        {/* Exploration Worksheet Panel */}
        <ExplorationWorksheet
          isOpen={worksheetOpen}
          searchTerm={worksheetSearchTerm}
          activeTab={worksheetActiveTab}
          parentWordId={worksheetParentWordId}
          searchNonce={worksheetSearchNonce}
          rhythmicStability={convertStabilityLevelToRhythmic(song?.settings?.prosody_settings?.rhythmic_stability)}
          onClose={closeWorksheet}
          onSearchChange={setWorksheetSearchTerm}
          onTabChange={setWorksheetActiveTab}
          closeOnBackdrop={true}
          onCollectAndExploreRhymes={(word) => {
            // User clicked "Collect & Explore Rhymes" in thesaurus view
            // Switch to rhymes tab with the word pre-filled
            setWorksheetSearchTerm(word)
            setWorksheetActiveTab('rhymes')
          }}
        />

        {/* Point of View Settings Modal removed - POV settings now handled by full settings panel */}
      </div>
    )
  }
)

CleanSongEditor.displayName = 'CleanSongEditor'

export { CleanSongEditor }
export default CleanSongEditor
